import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { State } from "./types";
import useStyles from "./styles";
import { UserContext } from "../../../UserProvider";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import {
  hasRole,
  toUserOrderWriteModel,
  UserOrder,
  UserOrderItemId,
  UserOrderState
} from "../../../models/User";
import { permissions } from "../../../constants";
import { AlertTitle } from "@material-ui/lab";
import Alert from "@material-ui/lab/Alert";
import Progress from "../../../components/information/progress";
import { thunkFetchUserOrders } from "../../../store/users/actions";
import { Wrapper } from "./components/Wrapper";
import {
  Card,
  CardContent,
  Divider,
  Fab,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import CheckIcon from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";
import LockOpenIcon from "@material-ui/icons/LockOpen";

import { Storage } from "../../../storage/userOrder";
import * as UserOrderApi from "../../../api/userOrderApi";
import { isOk } from "../../../result";
import { ProductMinimalModel } from "../../../models/Product";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import { StorePickupBoxMultiSelect } from "../../../components/selects/StorePickupBoxMultiSelect";
import { StorePickupBox } from "../../../models/Store";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import moment from "moment";
import { useI18n } from "../../../hooks/useI18n";

export const UserOrderOverview = () => {
  const navigate = useNavigate();
  const params = useParams();
  const classes = useStyles();
  const { t } = useI18n();

  const dispatch = useDispatch();

  const [state, setState] = useState<State>({ type: "INITIAL" });
  const currentUser = useContext(UserContext);
  const userOrders =
    useSelector<AppState, ReadonlyArray<UserOrder>>(s => s.userOrders) ||
    undefined;

  useEffect(() => {
    switch (state.type) {
      case "INITIAL":
        if (hasRole(currentUser, permissions.stores)) {
          if (params.userOrderId) {
            return setState({
              type: "LOAD_ORDER",
              id: params.userOrderId
            });
          } else {
            return setState({
              type: "UNEXPECTED_ERROR",
              problem: {
                status: 404,
                title: "No valid identifier provided.",
                detail: "The `userOrderId` is missing in the URL.",
                errors: [],
                traceId: ""
              }
            });
          }
        }
        return setState({ type: "NOT_ALLOWED" });

      case "LOAD_ORDER":
        dispatch(thunkFetchUserOrders());
        break;

      case "STARTING":
        const started = {
          ...state.order,
          state: UserOrderState.Processing
        };

        UserOrderApi.updateOrder(toUserOrderWriteModel(started)).then(
          result => {
            if (isOk(result)) {
              return setState({
                type: "GATHER",
                order: started
              });
            } else {
              return setState({
                type: "UNEXPECTED_ERROR",
                problem: result.details
              });
            }
          }
        );
        break;

      case "SAVING":
        const completed = {
          ...state.order,
          state: UserOrderState.AwaitingPickup
        };

        UserOrderApi.updateOrder(toUserOrderWriteModel(completed)).then(
          result => {
            if (isOk(result)) {
              Storage.userOrder = undefined;
              return setState({
                type: "DONE",
                order: completed
              });
            } else {
              return setState({
                type: "UNEXPECTED_ERROR",
                problem: result.details
              });
            }
          }
        );
        break;

      case "CANCELLING":
        const cancelled = {
          ...state.order,
          state: UserOrderState.Cancelled
        };

        UserOrderApi.updateOrder(toUserOrderWriteModel(cancelled)).then(
          result => {
            if (isOk(result)) {
              return navigate(`/stores/${state.order.store.id}/user-orders`);
            } else {
              return setState({
                type: "UNEXPECTED_ERROR",
                problem: result.details
              });
            }
          }
        );
        break;

      case "OPENING_BOXES":
        UserOrderApi.openPickupBoxes(state.order).then(result => {
          if (isOk(result)) {
            return setState({ ...state, type: "DONE" });
          } else {
            return setState({
              type: "UNEXPECTED_ERROR",
              problem: result.details
            });
          }
        });

        break;
    }
  }, [state.type]);

  useEffect(() => {
    if (state.type !== "LOAD_ORDER") {
      return;
    }

    let order = userOrders.find(x => x.id === state.id);

    if (order) {
      order = {
        ...order,
        items: order.items.map(i => ({
          ...i,
          deliveredQuantity: i.orderedQuantity
        }))
      }; // we assume the same quantity will be delivered as has been ordered. otherwise BC will change
      order = order.id === Storage.userOrder?.id ? Storage.userOrder : order; // if the order exists in local storage, then use that version

      switch (order.state) {
        case UserOrderState.Ordered:
          return setState({
            type: "START",
            order: order
          });

        case UserOrderState.Processing: {
          return setState({
            type: "GATHER",
            order: order
          });
        }

        default: {
          return setState({
            type: "DONE",
            order: order
          });
        }
      }
    } else {
      return setState({
        type: "UNEXPECTED_ERROR",
        problem: {
          status: 404,
          title: "No order found.",
          detail: `No order found with id '${state.id}'. Make sure the URL contains a proper identifier!`,
          errors: [],
          traceId: ""
        }
      });
    }
  }, [userOrders]);

  const renderSecondaryText = (product: ProductMinimalModel): string => {
    let rendered = "";

    if (product.brandName) {
      rendered += product.brandName;
    }

    if (product.size) {
      if (rendered) {
        rendered += ", ";
      }

      rendered += product.size;
    }

    if (rendered) {
      rendered += ", ";
    }

    rendered += product.gtin;

    return rendered;
  };

  const renderProductFullname = (product: ProductMinimalModel): string => {
    let rendered = "";

    if (product.brandName && !product.name.startsWith(product.brandName)) {
      rendered += product.brandName;
    }

    rendered += ` ${product.name}`;

    if (product.size) {
      rendered += `, ${product.size}`;
    }

    rendered += `, ${product.gtin}`;

    return rendered.trim();
  };

  const handleBack = async () => {
    switch (state.type) {
      case "CONFIRM":
        return setState({ ...state, type: "GATHER" });
    }
  };

  const handleNext = () => {
    switch (state.type) {
      case "START":
        return setState({ ...state, type: "STARTING" });

      case "GATHER":
        return setState({ ...state, type: "CONFIRM" });

      case "CONFIRM":
        return setState({ ...state, type: "CONFIRM_DIALOG" });

      case "CONFIRM_DIALOG":
        return setState({ ...state, type: "SAVING" });
    }
  };

  const handleCancel = () => {
    switch (state.type) {
      case "DONE":
        return setState({ ...state, type: "CANCELLING" });
    }
  };

  const handleOpenBoxes = () => {
    switch (state.type) {
      case "DONE":
        return setState({ ...state, type: "OPENING_BOXES" });
    }
  };

  const handleDeliveredQuantityChange = (
    id: UserOrderItemId,
    newValue?: number
  ) => {
    if (state.type !== "GATHER") {
      return;
    }

    const order = state.order;
    const index = order.items.findIndex(x => x.id === id);
    order.items[index].deliveredQuantity = newValue ?? 0;

    Storage.userOrder = order;
    return setState({ ...state, order });
  };

  const handlePickupBoxesChange = (boxes: ReadonlyArray<StorePickupBox>) => {
    if (state.type !== "CONFIRM") {
      return;
    }

    const order = state.order;

    order.pickupBoxes = boxes;

    Storage.userOrder = order;
    return setState({ ...state, order });
  };

  const handleDeadlineChange = (deadline?: Date) => {
    if (state.type !== "CONFIRM") {
      return;
    }

    const order = state.order;

    order.deadline = deadline;

    Storage.userOrder = order;
    return setState({ ...state, order });
  };

  const handleCommentChange = (comment?: string) => {
    if (state.type !== "CONFIRM") {
      return;
    }

    const order = state.order;

    order.comment = comment;

    Storage.userOrder = order;
    return setState({ ...state, order });
  };

  const handleConfirm = (): boolean => {
    handleNext();
    return true;
  };

  switch (state.type) {
    case "NOT_ALLOWED":
      return (
        <Alert severity="warning">
          {t("common.messages.notAllowedToSeeThisView")}
        </Alert>
      );

    case "UNEXPECTED_ERROR":
      return (
        <Alert severity="error">
          <AlertTitle>
            {state.problem.status}, {state.problem.title}
          </AlertTitle>
          {state.problem.detail} <strong>{state.problem.traceId}</strong>
        </Alert>
      );

    case "INITIAL":
    case "LOAD_ORDER":
      return (
        <div className={classes.loading}>
          <Progress isModule={true} />
        </div>
      );

    case "START":
      return (
        <Wrapper state={state}>
          <Alert severity="info">
            When pressing <b>START</b> below the order will be put in{" "}
            <i>Processing</i> notifying the customer that something is
            happening. Past this state the customer cannot cancel the order
            anymore.
          </Alert>
          <Card className={classes.itemsSection} variant={"outlined"}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Items
              </Typography>
              <List className={classes.list}>
                {state.order.items.map(item => (
                  <>
                    <Divider />
                    <ListItem key={item.id} className={classes.listItemTight}>
                      <ListItemText
                        primary={`${item.orderedQuantity} x`}
                        secondary={renderProductFullname(item.product)}
                      />
                    </ListItem>
                  </>
                ))}
              </List>
            </CardContent>
          </Card>

          <div className={classes.fabButtons}>
            <Fab
              variant="extended"
              className={classes.nextButton}
              aria-label="start"
              onClick={handleNext}
            >
              Start
              <NavigateNextIcon />
            </Fab>
          </div>
        </Wrapper>
      );

    case "STARTING":
      return (
        <Wrapper state={state}>
          <div className={classes.loading}>
            <Progress isModule={true} />
          </div>
        </Wrapper>
      );

    case "GATHER":
      return (
        <Wrapper state={state}>
          <Alert severity="info">
            Gather all the products shown below and adjust the delivered amount
            if needed.
          </Alert>
          <List className={classes.list}>
            {state.order.items.map(item => (
              <ListItem key={item.id}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Divider className={classes.divider} />
                    <ListItemText
                      primary={item.product.name}
                      secondary={renderSecondaryText(item.product)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      className={classes.field}
                      label="Ordered"
                      type="number"
                      defaultValue={item.orderedQuantity}
                      disabled={true}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">st</InputAdornment>
                        )
                      }}
                      helperText={t(
                        "common.formFields.amountOrderedByCustomer"
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      className={classes.field}
                      label="Delivered"
                      type="number"
                      defaultValue={item.deliveredQuantity}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">st</InputAdornment>
                        )
                      }}
                      onChange={e =>
                        handleDeliveredQuantityChange(
                          item.id,
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value)
                        )
                      }
                      helperText={t(
                        "common.formFields.amountActuallyDelivered"
                      )}
                    />
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>
          <div className={classes.fabButtons}>
            <Fab
              className={classes.nextButton}
              aria-label="next"
              onClick={handleNext}
            >
              <NavigateNextIcon />
            </Fab>
          </div>
        </Wrapper>
      );

    case "CONFIRM":
    case "CONFIRM_DIALOG":
      return (
        <Wrapper state={state}>
          <Alert severity="info">
            Confirm the <b>amounts</b> below, pick some <b>box(es)</b> to put
            the bag(s) in, pick a <b>deadline</b> for the pickup, and
            additionally write a <b>comment</b> for the customer.
          </Alert>
          <Card className={classes.itemsSection} variant={"outlined"}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Items
              </Typography>
              <List>
                {state.order.items.map(item => (
                  <>
                    <Divider />
                    <ListItem key={item.id} style={{ padding: 0 }}>
                      <ListItemText
                        primary={`${item.deliveredQuantity} x`}
                        secondary={renderProductFullname(item.product)}
                      />
                    </ListItem>
                  </>
                ))}
              </List>
            </CardContent>
          </Card>
          <Card className={classes.otherSection} variant={"outlined"}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Other
              </Typography>
              <StorePickupBoxMultiSelect
                className={classes.field}
                label={"Box(es) *"}
                storeId={state.order.store.id}
                value={state.order.pickupBoxes.map(x => x.id)}
                onSelected={handlePickupBoxesChange}
              />
              <KeyboardDateTimePicker
                className={classes.field}
                margin="normal"
                id="deadline-picker-dialog"
                label="Deadline *"
                format="YYYY-MM-DD HH:mm"
                value={state.order.deadline ?? null}
                onChange={(date: MaterialUiPickersDate) => {
                  if (date && date.isValid()) {
                    return handleDeadlineChange(date.toDate());
                  }
                  // else {
                  //   return handleDeadlineChange(undefined)
                  // }
                }}
                ampm={false}
                KeyboardButtonProps={{
                  "aria-label": t("common.actions.changeDeadline")
                }}
              />
              <TextField
                className={classes.field}
                label="Comment"
                multiline
                type="text"
                defaultValue={state.order.comment}
                onChange={e => handleCommentChange(e.target.value)}
              />
            </CardContent>
          </Card>
          <div className={classes.fabButtons}>
            <Fab
              className={classes.backButton}
              color="primary"
              aria-label="back"
              onClick={handleBack}
            >
              <NavigateBeforeIcon />
            </Fab>
            <Fab
              className={classes.nextButton}
              aria-label="next"
              onClick={handleNext}
              disabled={
                !state.order.deadline ||
                !state.order.pickupBoxes ||
                !state.order.pickupBoxes.length ||
                state.order.items.reduce(
                  (a, b) => a + (b.deliveredQuantity ? b.deliveredQuantity : 0),
                  0
                ) === 0
              }
            >
              <CheckIcon />
            </Fab>
          </div>
          <ConfirmDialog
            visible={state.type === "CONFIRM_DIALOG"}
            titleText={"Are you sure?"}
            onCancel={() => setState({ ...state, type: "CONFIRM" })}
            onConfirm={handleConfirm}
            cancelText={"No"}
            confirmText={"Yes"}
          >
            The order will be locked for you and the customer gets a
            notification it can be picked up. Continue?
          </ConfirmDialog>
        </Wrapper>
      );

    case "SAVING":
      return (
        <Wrapper state={state}>
          <div className={classes.loading}>
            <Progress isModule={true} />
          </div>
        </Wrapper>
      );

    case "DONE":
      return (
        <Wrapper state={state}>
          {!state.order.deadline || moment(state.order.deadline) > moment() ? (
            <Alert severity="info">
              The order is now considered <b>done</b> from your part and
              currently has the state <b>{state.order.state}</b>.
            </Alert>
          ) : null}
          {state.order.deadline && moment(state.order.deadline) < moment() ? (
            <Alert severity="warning">
              The order has passed the <b>deadline</b> for pickup. When pressing
              the <b>cancel</b> below, the order will be cancelled and the boxes
              opened.
            </Alert>
          ) : null}
          <Card className={classes.itemsSection} variant={"outlined"}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Info
              </Typography>
              <List>
                <Divider />
                <ListItem
                  key={"user"}
                  component={Link}
                  to={`/users/${state.order.user.id}`}
                  className={classes.listItemTight}
                >
                  <ListItemText
                    primary={"User"}
                    secondary={`${state.order.user.firstName} ${state.order.user.lastName}`}
                  />
                </ListItem>
                <Divider />
                <ListItem key={"boxes"} className={classes.listItemTight}>
                  <ListItemText
                    primary={"Box(es)"}
                    secondary={state.order.pickupBoxes
                      .map(box => `Slot ${box.slot}`)
                      .join(", ")}
                  />
                </ListItem>
                <Divider />
                <ListItem key={"comment"} className={classes.listItemTight}>
                  <ListItemText
                    primary={"Comment"}
                    secondary={state.order.comment ?? "..."}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          <Card className={classes.itemsSection} variant={"outlined"}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Items
              </Typography>
              <List className={classes.list}>
                {state.order.items.map(item => (
                  <>
                    <Divider />
                    <ListItem key={item.id} className={classes.listItemTight}>
                      <ListItemText
                        primary={`${item.deliveredQuantity} x`}
                        secondary={renderProductFullname(item.product)}
                      />
                    </ListItem>
                  </>
                ))}
              </List>
            </CardContent>
          </Card>
          <div className={classes.fabButtons}>
            <Fab
              variant="extended"
              color={"primary"}
              aria-label="open-boxes"
              onClick={handleOpenBoxes}
            >
              Open boxes
              <LockOpenIcon />
            </Fab>
            {state.order.deadline && moment(state.order.deadline) < moment() ? (
              <Fab
                variant="extended"
                className={classes.cancelButton}
                aria-label="cancel"
                onClick={handleCancel}
              >
                Cancel
                <DeleteIcon />
              </Fab>
            ) : null}
          </div>
        </Wrapper>
      );

    case "CANCELLING":
    case "OPENING_BOXES":
      return (
        <Wrapper state={state}>
          <div className={classes.loading}>
            <Progress isModule={true} />
          </div>
        </Wrapper>
      );
  }
};
